import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoggingService } from '@services/logging.service';
import { catchError, take } from 'rxjs/operators';
import { firstValueFrom, Subject, Subscription, throwError, timer } from 'rxjs';
import { PredictionStatus } from './prediction-welcome/prediction-status.model';
import { PredictionAcceptance } from './comparable-details/prediction-acceptance.model';
import { ConfigService } from '@services/config.service';
export const GET_STATUS_URL = '/api/PredictionEngine/GetStatus/';
export const EXECUTE_URL = '/api/PredictionEngine/Execute/';
export const SAVE_ACCEPTANCE_URL = '/api/PredictionEngine/SaveAcceptance/';
export const ABORT_PREDICTION_PROCESS_URL = '/api/PredictionEngine/abort/';

@Injectable({
  providedIn: 'root',
})
export class PredictionEngineService {
  public predictionEngineTimeOut$: Subject<boolean> = new Subject<boolean>();
  private predictionEngineTimeOutTimerSbs: Subscription;
  public maxPredictionRetry = 3;
  public predictionEngineTimeoutMin = 5;
  constructor(private http: HttpClient, private logger: LoggingService, private configService: ConfigService) {
    this.configService
      .getConfigValue(ConfigService.PREDICTION_ENGINE_TIMEOUT_MINUTES)
      .pipe(take(1))
      .subscribe((val) => {
        this.predictionEngineTimeoutMin = +val;
      });

    this.configService
      .getConfigValue(ConfigService.PREDICTION_ENGINE_MAX_RETRY)
      .pipe(take(1))
      .subscribe((val) => {
        this.maxPredictionRetry = +val;
      });
  }

  getPredictionStatus(studySearchStrategyId: number) {
    const url = GET_STATUS_URL + studySearchStrategyId;
    this.logger.info(this.constructor.name, 'getPredictionStatus', 'Start');
    return this.http.get<PredictionStatus>(url).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, url, error);
        return throwError(() => error);
      })
    );
  }

  initializePredictionProcess(studySearchStrategyId: number, timeOut: number, resetCounter = false) {
    const url = EXECUTE_URL + studySearchStrategyId + '/' + resetCounter;
    this.logger.info(this.constructor.name, 'initializePredictionProcess', 'Start');
    this.createPredictionEngineTimeOutSbs(timeOut);
    return this.http.post(url, null).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, url, error);
        return throwError(() => error);
      })
    );
  }

  public createPredictionEngineTimeOutSbs(timeout: number, value = true) {
    this.predictionEngineTimeOutTimerSbs = timer(timeout * 60 * 1000).subscribe(() => {
      this.predictionEngineTimeOut$.next(value);
    });
  }

  public cancelPredictionEngineTimeOut() {
    if (this.predictionEngineTimeOutTimerSbs) {
      this.predictionEngineTimeOutTimerSbs.unsubscribe();
    }
  }

  public abort(searchStrategyId: number) {
    this.logger.info(this.constructor.name, 'abort', 'abort');
    return this.http.get(ABORT_PREDICTION_PROCESS_URL + searchStrategyId).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, ABORT_PREDICTION_PROCESS_URL, error);
        return throwError(() => error);
      })
    );
  }

  async saveAcceptanceOfPrediction(predictionAcceptance: PredictionAcceptance) {
    const url = SAVE_ACCEPTANCE_URL;
    this.logger.info(this.constructor.name, 'saveAcceptanceOfPrediction', 'Start');
    return await firstValueFrom(this.http.post(url, predictionAcceptance)).catch((error) => {
      this.logger.httpError(this.constructor.name, url, error);
      return throwError(() => error);
    });
  }
}
